/**
 * Risk Register Form
 * Add and manage business risks with mitigation strategies
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RiskRegisterForm = ({ planId }) => {
  const [risks, setRisks] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRisk, setNewRisk] = useState({
    risk_category: 'market',
    risk_description: '',
    likelihood: 'medium',
    impact: 'medium',
    mitigation_strategy: ''
  });

  useEffect(() => {
    if (planId) {
      fetchRisks();
    }
  }, [planId]);

  const fetchRisks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/risk-register/plan/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRisks(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const addRisk = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/risk-register`, {
        plan_id: planId,
        ...newRisk
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddForm(false);
      setNewRisk({
        risk_category: 'market',
        risk_description: '',
        likelihood: 'medium',
        impact: 'medium',
        mitigation_strategy: ''
      });
      fetchRisks();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getRiskColor = (score) => {
    if (score >= 12) return 'bg-red-100 border-red-500 text-red-800';
    if (score >= 6) return 'bg-orange-100 border-orange-500 text-orange-800';
    return 'bg-yellow-100 border-yellow-500 text-yellow-800';
  };

  return (
    <div className="space-y-6 p-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Risk Register</h2>
          <p className="text-gray-600 mt-1">Identify and mitigate business risks</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          + Add Risk
        </button>
      </div>

      {/* Risk Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Risks</div>
          <div className="text-2xl font-bold">{risks.length}</div>
        </div>
        <div className="bg-red-50 rounded-lg shadow p-4">
          <div className="text-sm text-red-600">High Risk</div>
          <div className="text-2xl font-bold text-red-600">
            {risks.filter(r => r.risk_score >= 12).length}
          </div>
        </div>
        <div className="bg-orange-50 rounded-lg shadow p-4">
          <div className="text-sm text-orange-600">Medium Risk</div>
          <div className="text-2xl font-bold text-orange-600">
            {risks.filter(r => r.risk_score >= 6 && r.risk_score < 12).length}
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg shadow p-4">
          <div className="text-sm text-yellow-600">Low Risk</div>
          <div className="text-2xl font-bold text-yellow-600">
            {risks.filter(r => r.risk_score < 6).length}
          </div>
        </div>
      </div>

      {/* Risks List */}
      <div className="space-y-4">
        {risks.map((risk) => (
          <div key={risk.id} className={`border-l-4 rounded-lg p-6 ${getRiskColor(risk.risk_score)}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <span className="text-xs uppercase font-semibold">{risk.risk_category}</span>
                <h4 className="font-bold text-lg mt-1">{risk.risk_description}</h4>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-600">Risk Score</div>
                <div className="text-2xl font-bold">{risk.risk_score}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <span className="text-xs text-gray-600">Likelihood:</span>
                <span className="ml-2 font-medium capitalize">{risk.likelihood}</span>
              </div>
              <div>
                <span className="text-xs text-gray-600">Impact:</span>
                <span className="ml-2 font-medium capitalize">{risk.impact}</span>
              </div>
            </div>
            {risk.mitigation_strategy && (
              <div className="mt-3 p-3 bg-white bg-opacity-50 rounded">
                <div className="text-xs font-semibold mb-1">Mitigation Strategy:</div>
                <div className="text-sm">{risk.mitigation_strategy}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      {risks.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No risks identified yet. Click "+ Add Risk" to start.
        </div>
      )}

      {/* Add Risk Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Add New Risk</h3>
            <form onSubmit={addRisk}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Risk Category</label>
                  <select
                    value={newRisk.risk_category}
                    onChange={(e) => setNewRisk({...newRisk, risk_category: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="market">Market Risk</option>
                    <option value="financial">Financial Risk</option>
                    <option value="operational">Operational Risk</option>
                    <option value="regulatory">Regulatory Risk</option>
                    <option value="technical">Technical Risk</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Risk Description *</label>
                  <textarea
                    value={newRisk.risk_description}
                    onChange={(e) => setNewRisk({...newRisk, risk_description: e.target.value})}
                    placeholder="Describe the risk in detail..."
                    className="w-full border rounded px-3 py-2 h-24"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Likelihood</label>
                    <select
                      value={newRisk.likelihood}
                      onChange={(e) => setNewRisk({...newRisk, likelihood: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="very_high">Very High</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Impact</label>
                    <select
                      value={newRisk.impact}
                      onChange={(e) => setNewRisk({...newRisk, impact: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Mitigation Strategy</label>
                  <textarea
                    value={newRisk.mitigation_strategy}
                    onChange={(e) => setNewRisk({...newRisk, mitigation_strategy: e.target.value})}
                    placeholder="How will you prevent or minimize this risk?"
                    className="w-full border rounded px-3 py-2 h-24"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
                >
                  Add Risk
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="bg-gray-200 px-6 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskRegisterForm;